import {
  Paper,
  Title,
  Button,
  Table,
  Text,
  Badge,
  Stack,
  Group,
  ScrollArea
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconUsers, IconRefresh } from '@tabler/icons-react';
import { useAppStore } from '../stores/useAppStore';
import { generateEmployees, getEmployeeStats } from '../utils/employeeGenerator';

export function EmployeeGenerator() {
  const {
    employees,
    setEmployees,
    isGeneratingEmployees,
    setIsGeneratingEmployees,
    clearAssignments
  } = useAppStore();

  const handleGenerateEmployees = async () => {
    setIsGeneratingEmployees(true);
    // 従業員を再生成する場合は既存のシフトをクリア
    clearAssignments();
    
    try {
      // 従業員を生成
      const newEmployees = generateEmployees();
      const stats = getEmployeeStats(newEmployees);
      
      setEmployees(newEmployees);
      
      notifications.show({
        title: '従業員生成完了',
        message: `${newEmployees.length}名の従業員を生成しました（多能工${stats.multiSkilled}名、単能工${stats.singleSkilled}名）`,
        color: 'green'
      });
    } catch (error) {
      notifications.show({
        title: 'エラー',
        message: '従業員の生成に失敗しました',
        color: 'red'
      });
    } finally {
      setIsGeneratingEmployees(false);
    }
  };

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3} size="h4">
            <Group gap="xs">
              <IconUsers size={20} />
              従業員管理
            </Group>
          </Title>
          <Button
            onClick={handleGenerateEmployees}
            loading={isGeneratingEmployees}
            leftSection={<IconRefresh size={16} />}
          >
            従業員生成
          </Button>
        </Group>

        {employees.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            「従業員生成」ボタンをクリックして25名の従業員を生成してください
          </Text>
        ) : (
          <ScrollArea h={400}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>氏名</Table.Th>
                  <Table.Th>スキル</Table.Th>
                  <Table.Th>種別</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {employees.map((employee) => (
                  <Table.Tr key={employee.id}>
                    <Table.Td>
                      <Text size="sm" ff="mono">
                        {employee.id}
                      </Text>
                    </Table.Td>
                    <Table.Td>{employee.name}</Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        {employee.skills
                          .sort((a, b) => a.localeCompare(b))
                          .map((skill) => (
                            <Badge key={skill} size="sm" variant="light">
                              {skill}
                            </Badge>
                          ))
                        }
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={employee.skills.length > 1 ? 'blue' : 'gray'}
                        variant="light"
                      >
                        {employee.skills.length > 1 ? '多能工' : '単能工'}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}

        {employees.length > 0 && (() => {
          const stats = getEmployeeStats(employees);
          return (
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  総従業員数: {stats.total}名
                </Text>
                <Group gap="md">
                  <Badge color="blue" variant="light">
                    多能工: {stats.multiSkilled}名
                  </Badge>
                  <Badge color="gray" variant="light">
                    単能工: {stats.singleSkilled}名
                  </Badge>
                </Group>
              </Group>
              <Group gap="sm">
                <Text size="xs" c="dimmed">ライン別有資格者数:</Text>
                {Object.entries(stats.lineStats).map(([line, count]) => (
                  <Badge key={line} size="xs" variant="outline">
                    {line}: {count}名
                  </Badge>
                ))}
              </Group>
            </Stack>
          );
        })()}
      </Stack>
    </Paper>
  );
}